import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { BellRing, X } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { useI18n } from "../i18n";
import { api } from "../lib/api";
import { MembersTab } from "./admin/MembersTab";
import { SettingsPanel } from "./admin/settings/SettingsPanel";
import { MessagesTab } from "./admin/MessagesTab";
import { PaymentReviewTab } from "./admin/PaymentReviewTab";

const Stat = ({ label, value, testId }) => (
  <div className="lux-card p-5"><p className="text-xs uppercase tracking-widest text-muted-foreground">{label}</p><p className="mt-2 font-serif text-4xl font-semibold gold-text" data-testid={testId}>{value ?? "—"}</p></div>
);

const ReceiptAlert = ({ count, onReview, onDismiss }) => {
  const { t } = useI18n();
  return (
    <div className="mt-8 flex flex-wrap items-center gap-4 rounded-2xl border p-4 fade-up" style={{ borderColor: "var(--gold)", background: "var(--gold-soft)" }} data-testid="receipt-alert">
      <span className="relative flex h-10 w-10 items-center justify-center rounded-full" style={{ background: "var(--gold)" }}>
        <BellRing className="h-5 w-5 text-[#0A0D14]" />
        <span className="absolute -right-0.5 -top-0.5 h-3 w-3 animate-ping rounded-full bg-red-500" />
      </span>
      <p className="flex-1 font-medium" data-testid="receipt-alert-text">{t("adm_receipts_waiting", { n: count })}</p>
      <button onClick={onReview} className="btn-gold !py-2" data-testid="receipt-alert-review-btn">{t("adm_review_now")}</button>
      <button onClick={onDismiss} aria-label="Dismiss" className="rounded-full p-1.5 hover:bg-accent" data-testid="receipt-alert-dismiss"><X className="h-4 w-4" /></button>
    </div>
  );
};

export default function Admin() {
  const { t } = useI18n();
  const [summary, setSummary] = useState(null);
  const [tab, setTab] = useState("members");
  const [dismissed, setDismissed] = useState(-1);
  const prev = useRef(null);
  const loadSummary = useCallback(() => api.get("/admin/summary").then((r) => setSummary(r.data)), []);
  useEffect(() => {
    loadSummary();
    const id = setInterval(loadSummary, 20000);
    return () => clearInterval(id);
  }, [loadSummary]);
  const count = summary?.awaiting_payment_review ?? 0;
  useEffect(() => {
    if (!summary) return;
    if (prev.current !== null && count > prev.current) {
      toast.info(t("adm_new_receipt_toast"), { action: { label: t("adm_review_now"), onClick: () => setTab("payments") } });
    }
    prev.current = count;
  }, [summary, count, t]);
  return (
    <section className="mx-auto max-w-7xl px-5 py-12 lg:px-8">
      <p className="eyebrow">Helping Hands</p>
      <h1 className="mt-2 font-serif text-4xl font-bold sm:text-5xl" data-testid="admin-title">{t("admin_title")}</h1>
      <p className="mt-2 text-muted-foreground">{t("admin_sub")}</p>
      {count > 0 && dismissed !== count && <ReceiptAlert count={count} onReview={() => setTab("payments")} onDismiss={() => setDismissed(count)} />}
      <div className="mt-8 grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-6">
        <Stat label={t("adm_pending")} value={summary?.pending} testId="admin-stat-pending" />
        <Stat label={t("adm_approved")} value={summary?.approved} testId="admin-stat-approved" />
        <Stat label={t("adm_rejected")} value={summary?.rejected} testId="admin-stat-rejected" />
        <Stat label={t("adm_blocked")} value={summary?.blocked} testId="admin-stat-blocked" />
        <Stat label={t("adm_paid")} value={summary?.paid} testId="admin-stat-paid" />
        <Stat label={t("adm_review")} value={summary?.awaiting_payment_review} testId="admin-stat-review" />
      </div>
      <Tabs value={tab} onValueChange={setTab} className="mt-10">
        <TabsList>
          <TabsTrigger value="members" data-testid="admin-tab-members">{t("adm_members")}</TabsTrigger>
          <TabsTrigger value="payments" data-testid="admin-tab-payments">{t("adm_payment_review")}{summary?.awaiting_payment_review > 0 && <span className="ml-2 rounded-full px-1.5 text-[10px] font-bold" style={{ background: "var(--gold)", color: "#0A0D14" }} data-testid="admin-review-count">{summary.awaiting_payment_review}</span>}</TabsTrigger>
          <TabsTrigger value="messages" data-testid="admin-tab-messages">{t("adm_messages")}</TabsTrigger>
          <TabsTrigger value="settings" data-testid="admin-tab-settings">{t("adm_settings")}</TabsTrigger>
        </TabsList>
        <TabsContent value="members" className="mt-6"><MembersTab onChange={loadSummary} /></TabsContent>
        <TabsContent value="payments" className="mt-6"><PaymentReviewTab key={count} onChange={loadSummary} /></TabsContent>
        <TabsContent value="messages" className="mt-6"><MessagesTab /></TabsContent>
        <TabsContent value="settings" className="mt-6"><SettingsPanel /></TabsContent>
      </Tabs>
    </section>
  );
}
